"use client";

import React from "react";
import { ArrowRight, BookOpen, CircleCheck as CheckCircle2, Download, FileSpreadsheet, FileText, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const reportSpecs = [{
  label: "Format",
  value: "PDF & Data Annex"
}, {
  label: "Length",
  value: "68 pages"
}, {
  label: "Sample size",
  value: "1,200 enterprises"
}, {
  label: "Methodology",
  value: "Double-blind peer review"
}];
const keyTakeaways = [{
  title: "Procurement cycles lengthened by 24%",
  description: "Enterprise buying committees added an average of 1.8 stakeholders to sign-offs over the past year."
}, {
  title: "Tool consolidation leads priority lists",
  description: "62% of executives intend to replace multi-vendor sprawl with integrated single-platform suites."
}, {
  title: "Direct vendor research replaces ad channels",
  description: "Decision-makers consult independent benchmark studies and peer whitepapers before engaging sales teams."
}];
export default function Spotlight() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="featured-resource-spotlight" data-nav="light" className="relative overflow-hidden bg-background py-16 sm:py-20 lg:py-24 text-foreground border-b border-border">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        {/* Section Header Masthead */}
        <div className="mb-10 lg:mb-14">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-8 bg-primary" />
            <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground font-sans">
              Flagship Annual Research
            </span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-border pb-4">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-serif text-foreground">
                Featured resource spotlight
              </h2>
            </div>
            <p className="text-sm text-muted-foreground font-sans max-w-md">
              Peer-reviewed annual intelligence delivered without commercial bias or vendor sponsorship.
            </p>
          </div>
          {/* Masthead rule dual-line detail */}
          <div className="h-[0.5px] w-full bg-border mt-1" />
        </div>

        {/* Spotlight Card Presentation */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="rounded-md border border-border bg-card shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            {/* Left Column: 3:4 Spine-Framed Cover Graphic */}
            <div className="lg:col-span-5 bg-muted/40 p-6 sm:p-10 flex flex-col justify-center items-center border-b lg:border-b-0 lg:border-r border-border relative">
              <div className="relative w-full max-w-[320px] aspect-[3/4] rounded-md shadow-2xl overflow-hidden border border-border bg-primary group transition-transform duration-300 hover:-translate-y-1">
                {/* Book Spine Crease Gradient */}
                <div className="absolute inset-0 z-20 pointer-events-none bg-gradient-to-r from-black/25 via-white/10 to-transparent w-[14%]" />
                <div className="absolute left-[13%] top-0 bottom-0 w-px bg-white/15 z-20 pointer-events-none" />

                {/* Cover Image */}
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/featured_research_cover.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/featured_research_cover.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/featured_research_cover.png-wvc-sizes" alt="The 2025 B2B Decision-Maker Index publication cover" loading="lazy" decoding="async" className="w-full h-full object-cover" />

                {/* Overlay Text Plate on Book */}
                <div className="absolute inset-0 z-10 p-6 flex flex-col justify-between bg-gradient-to-t from-primary/95 via-primary/40 to-transparent text-primary-foreground">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono tracking-widest uppercase bg-primary-foreground/15 px-2 py-0.5 rounded-sm border border-primary-foreground/20">
                      Report • Q1
                    </span>
                    <span className="text-[10px] font-sans font-medium text-primary-foreground/80">
                      MinfoMedia Press
                    </span>
                  </div>

                  <div>
                    <span className="text-xs uppercase tracking-wider text-primary-foreground/75 font-mono block mb-1">
                      Annual Benchmark
                    </span>
                    <p className="text-xl sm:text-2xl font-semibold font-serif leading-tight">
                      The 2025 B2B Decision-Maker Index
                    </p>
                    <div className="mt-4 pt-3 border-t border-primary-foreground/20 flex items-center justify-between text-[11px] text-primary-foreground/75">
                      <span>1,200 Executive Interviews</span>
                      <span>Verified Dataset</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Publication Guarantee Badge below book */}
              <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground font-sans">
                <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
                <span>Zero sponsored vendor placement • Fully open data</span>
              </div>
            </div>

            {/* Right Column: Executive Summary, Findings, Specs & Actions */}
            <div className="lg:col-span-7 p-6 sm:p-10 lg:p-12 flex flex-col justify-between">
              <div>
                {/* Meta pills */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="rounded-full bg-secondary text-secondary-foreground text-xs font-semibold px-3 py-1 uppercase tracking-wider font-sans">
                    Annual Index
                  </span>
                  <span className="rounded-sm border border-border bg-card text-muted-foreground text-[11px] font-mono px-2 py-0.5">
                    Edition 2025
                  </span>
                  <span className="rounded-sm border border-border bg-card text-muted-foreground text-[11px] font-mono px-2 py-0.5">
                    Unrestricted Access
                  </span>
                </div>

                {/* Main Title & Executive Summary */}
                <h3 className="text-2xl sm:text-3xl font-semibold font-serif text-foreground tracking-tight leading-snug">
                  The 2025 B2B decision-maker index
                </h3>

                <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
                  An independent analysis of enterprise procurement shifts, tech stack consolidation, and how buying committees evaluate strategic partners across 1,200 organizations.
                </p>

                {/* Key Findings List */}
                <div className="mt-8 border-t border-border pt-6">
                  <h4 className="text-xs uppercase tracking-wider font-semibold font-sans text-foreground mb-4">
                    Core findings inside this report
                  </h4>
                  <ul className="space-y-4">
                    {keyTakeaways.map((item, index) => <motion.li key={item.title} variants={itemVariants} data-index={index} className="flex items-start gap-3 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                        <div>
                          <span className="font-semibold text-foreground font-sans">
                            {item.title}:{" "}
                          </span>
                          <span className="text-muted-foreground font-sans">
                            {item.description}
                          </span>
                        </div>
                      </motion.li>)}
                  </ul>
                </div>

                {/* Report Format Specifications Grid */}
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-muted/30 p-4 rounded-sm border border-border">
                  {reportSpecs.map((spec, index) => <div key={spec.label} data-index={index} className="flex flex-col">
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">
                        {spec.label}
                      </span>
                      <span className="text-xs font-semibold text-foreground font-sans mt-0.5">
                        {spec.value}
                      </span>
                    </div>)}
                </div>
              </div>

              {/* Call to Actions */}
              <div className="mt-10 pt-6 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Button asChild className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 px-7 py-3.5 h-auto text-sm font-medium transition-[background-color,box-shadow]">
                  <Link to="/ResearchReportsPage">
                    <Download className="w-4 h-4 mr-1.5" />
                    <span>Access report</span>
                  </Link>
                </Button>

                <Button asChild variant="outline" className="rounded-sm border-border bg-card text-foreground hover:bg-muted/50 px-6 py-3.5 h-auto text-sm font-medium transition-colors">
                  <Link to="/ResourcesPage">
                    <BookOpen className="w-4 h-4 mr-1.5" />
                    <span>Browse resource library</span>
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Link>
                </Button>

                <span className="text-xs text-muted-foreground font-mono sm:ml-auto text-center sm:text-right pt-2 sm:pt-0">
                  No sign-up mandatory for executive summary
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Secondary Publication Quick Nav Ribbon */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-sm border border-border bg-card/60 flex items-start gap-3">
            <div className="p-2 rounded-sm bg-muted text-foreground">
              <FileSpreadsheet className="w-4 h-4" />
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground font-sans">
                Raw data worksheets
              </p>
              <p className="text-xs text-muted-foreground font-sans mt-0.5">
                Download anonymized survey metrics in CSV format.
              </p>
            </div>
          </div>

          <div className="p-4 rounded-sm border border-border bg-card/60 flex items-start gap-3">
            <div className="p-2 rounded-sm bg-muted text-foreground">
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground font-sans">
                Methodology appendix
              </p>
              <p className="text-xs text-muted-foreground font-sans mt-0.5">
                Read confidence intervals and panel selection criteria.
              </p>
            </div>
          </div>

          <div className="p-4 rounded-sm border border-border bg-card/60 flex items-start gap-3">
            <div className="p-2 rounded-sm bg-muted text-foreground">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground font-sans">
                Editorial integrity
              </p>
              <p className="text-xs text-muted-foreground font-sans mt-0.5">
                Funded by MinfoMedia research endowments.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}